import { useEffect, useState } from 'react';

import {
    Card,
    CardHeader,
    Dropdown,
    Field,
    Input,
    StatusPill,
    Table,
    TableBody,
    TableCell,
    TableFootNote,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@/design-system';
import {
    formatMoney,
    INVOICE_STATUS_LABELS,
    INVOICE_STATUS_TONES,
    INVOICE_STATUSES,
    type BillingIntegration,
    type Invoice,
    type InvoiceFilters,
    type InvoiceStatus,
} from '@/entities/invoice';
import type { Page } from '@/shared/api/envelope';
import { Pager } from '@/shared/ui';

import { billingCopy } from '../copy';
import { InvoiceDetail } from './InvoiceDetail';

export interface InvoicesCardProps {
    page: Page<Invoice>;
    filters: InvoiceFilters;
    onFiltersChange: (filters: InvoiceFilters) => void;
    /** Een andere pagina of een ander filter laadt; de lijst blijft staan. */
    busy?: boolean;
    /** De koppeling met de boekhouding; haar zin staat onder de titel. */
    integration: BillingIntegration | null;
    /** Uitgeven (versturen) is voorbehouden aan de eigenaar. */
    canSend: boolean;
}

/** Het statusfilter kent één keuze die geen status is: alles wat nog betaald moet worden. */
const OUTSTANDING = 'outstanding';

/** Hoe lang het typen van een factuurnummer mag pauzeren voordat er gezocht wordt. */
const SEARCH_DELAY_MS = 300;

/**
 * Facturentabel (scherm 10, linkerkolom).
 *
 * Het raster volgt het ontwerp (nummer, object, dan status en bedrag rechts),
 * met iets bredere kolommen: een concept heet `concept 4F2K9Q`, "Gecrediteerd"
 * is de langste status en bedragen hebben soms centen - en een tabel clipt nooit
 * (principe 11). Onder die breedte scrollt hij binnen zijn kaart.
 *
 * Acties per factuur staan niet in een extra kolom maar in de balk onder de tabel,
 * met ruimte om te zeggen wat een actie doet voordat hij gebeurt.
 */
export function InvoicesCard({
    page,
    filters,
    onFiltersChange,
    busy = false,
    integration,
    canSend,
}: InvoicesCardProps) {
    const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
    const [numberInput, setNumberInput] = useState(filters.number ?? '');
    const [notice, setNotice] = useState<string | null>(null);

    const selected = page.items.find((invoice) => invoice.uuid === selectedUuid) ?? null;
    const filtered = Boolean(filters.status || filters.outstanding || filters.number);

    useEffect(() => {
        const next = numberInput.trim();

        if (next === (filters.number ?? '')) {
            return;
        }

        const timer = window.setTimeout(
            () => onFiltersChange({ ...filters, number: next, page: 1 }),
            SEARCH_DELAY_MS,
        );

        return () => window.clearTimeout(timer);
    }, [numberInput, filters, onFiltersChange]);

    return (
        <Card clip as="section" aria-labelledby="invoices-title">
            <CardHeader
                title={<span id="invoices-title">{billingCopy.invoicesTitle}</span>}
                description={integration?.message}
            />

            <div className="flex flex-wrap items-end gap-2 border-b border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3">
                <Field label={billingCopy.filterStatus} size="sm" className="min-w-[160px]">
                    <Dropdown<InvoiceStatus | typeof OUTSTANDING | ''>
                        value={filters.outstanding ? OUTSTANDING : (filters.status ?? '')}
                        options={[
                            { value: '', label: billingCopy.filterStatusAll },
                            { value: OUTSTANDING, label: billingCopy.filterOutstanding },
                            ...INVOICE_STATUSES.map((status) => ({
                                value: status,
                                label: INVOICE_STATUS_LABELS[status],
                            })),
                        ]}
                        onChange={(value) =>
                            onFiltersChange({
                                ...filters,
                                status: value === OUTSTANDING ? '' : value,
                                outstanding: value === OUTSTANDING,
                                page: 1,
                            })
                        }
                    />
                </Field>

                <Field label={billingCopy.filterNumber} size="sm" className="min-w-[180px] flex-1">
                    <Input
                        mono
                        value={numberInput}
                        placeholder={billingCopy.filterNumberPlaceholder}
                        onChange={(event) => setNumberInput(event.target.value)}
                    />
                </Field>
            </div>

            <Table
                columns="124px minmax(0,1.3fr) minmax(0,1fr) 112px 112px"
                minWidth={700}
                label={billingCopy.invoicesTableLabel}
                aria-busy={busy || undefined}
            >
                <TableHead>
                    <TableHeaderCell>{billingCopy.columnNumber}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnObject}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnClient}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnStatus}</TableHeaderCell>
                    <TableHeaderCell align="end">{billingCopy.columnAmount}</TableHeaderCell>
                </TableHead>
                <TableBody>
                    {page.items.map((invoice) => (
                        <TableRow
                            key={invoice.uuid}
                            selected={invoice.uuid === selectedUuid}
                            onSelect={() => {
                                setSelectedUuid(
                                    invoice.uuid === selectedUuid ? null : invoice.uuid,
                                );
                                setNotice(null);
                            }}
                        >
                            <TableCell mono muted truncate>
                                {invoice.label}
                            </TableCell>
                            <TableCell emphasis truncate>
                                {invoice.address ?? billingCopy.noObject}
                            </TableCell>
                            <TableCell muted truncate>
                                {invoice.clientName ?? '—'}
                            </TableCell>
                            <TableCell>
                                <StatusPill
                                    tone={
                                        invoice.status
                                            ? INVOICE_STATUS_TONES[invoice.status]
                                            : 'neutral'
                                    }
                                    size="sm"
                                >
                                    {invoice.statusLabel}
                                </StatusPill>
                            </TableCell>
                            <TableCell mono align="end">
                                {formatMoney(invoice.total)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {page.items.length === 0 ? (
                <TableFootNote>
                    {filtered ? billingCopy.invoicesEmptyFiltered : billingCopy.invoicesEmpty}
                </TableFootNote>
            ) : null}

            {selected ? (
                <InvoiceDetail
                    key={selected.uuid}
                    invoice={selected}
                    canSend={canSend}
                    onDone={setNotice}
                />
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="m-0 border-t border-[var(--border-subtle-2)] px-4 py-[10px] text-[13px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            <Pager
                meta={page.meta}
                noun={billingCopy.invoicesNoun}
                busy={busy}
                onPage={(next) => onFiltersChange({ ...filters, page: next })}
            />
        </Card>
    );
}
